import React from 'react'
import { cx } from '@/utils/strings'
import './aura-card.css'

const DEFAULT_COLORS = ['var(--color-primary-300)', 'var(--color-teal-500)', 'var(--color-indigo-500)']

const BORDER_SIZE_MAP: Record<string, string> = {
  none: '0px',
  sm: '1px',
  md: '2px',
  lg: '4px',
}
const GLOW_SIZE_MAP: Record<string, string> = {
  none: '0px',
  sm: '0.5rem',
  md: '1rem',
  lg: '1.5rem',
}

type AuraCardProps = {
  as?: React.ElementType
  children: React.ReactNode
  className?: string
  /** Ordered list of colors for the rotating gradient. Defaults to the original red-orange palette. */
  colors?: string[]
  /** Border size preset. Defaults to 'md'. */
  borderSize?: 'none' | 'sm' | 'md' | 'lg'
  /** Glow size preset. Defaults to 'md'. */
  glowSize?: 'none' | 'sm' | 'md' | 'lg'
  /** Animation speed for the rotating gradient. Defaults to '6s'. */
  speed?: string
}

export default function AuraCard({
  as: Component = 'div',
  children,
  className,
  colors,
  borderSize = 'md',
  glowSize = 'md',
  speed = '6s',
}: AuraCardProps) {
  const palette = colors && colors.length >= 2 ? colors : DEFAULT_COLORS
  // repeat the first color at the end so the rotation loops seamlessly
  const stops = [...palette, palette[0]].join(', ')
  const border = BORDER_SIZE_MAP[borderSize]
  const blur = GLOW_SIZE_MAP[glowSize]

  return (
    <Component
      className={cx(
        'relative bg-primary-500/8 border-solid border-transparent',
        // ::before — rotating gradient border
        "before:absolute before:content-[''] before:rounded-[inherit] before:pointer-events-none",
        'before:[inset:calc(-1*var(--aura-border))] before:[border:solid_var(--aura-border)_transparent]',
        'before:[box-shadow:0_0_calc(3*var(--aura-blur))_rgb(0_0_0_/_0.001)]',
        'before:[background:conic-gradient(from_var(--aura-angle),_var(--aura-stops))_border-box]',
        'before:[mask:conic-gradient(red_0_0)_no-clip_subtract,_conic-gradient(red_0_0)_padding-box]',
        'before:animate-[aura-rotate_var(--aura-speed)_linear_infinite]',
        // ::after — same layer blurred into a glow
        "after:absolute after:content-[''] after:rounded-[inherit] after:pointer-events-none",
        'after:[inset:calc(-1*var(--aura-border))] after:[border:solid_var(--aura-border)_transparent]',
        'after:[box-shadow:0_0_calc(3*var(--aura-blur))_rgb(0_0_0_/_0.001)]',
        'after:[background:conic-gradient(from_var(--aura-angle),_var(--aura-stops))_border-box]',
        'after:[mask:conic-gradient(red_0_0)_no-clip_subtract,_conic-gradient(red_0_0)_padding-box]',
        'after:animate-[aura-rotate_var(--aura-speed)_linear_infinite] after:[filter:blur(var(--aura-blur))]',
        className
      )}
      style={
        {
          '--aura-border': border,
          '--aura-blur': blur,
          '--aura-stops': stops,
          '--aura-speed': speed,
          borderWidth: 'var(--aura-border)',
        } as React.CSSProperties
      }
    >
      {children}
    </Component>
  )
}
